import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useParams, useSearchParams } from 'react-router-dom'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ONLY_DATE_FORMAT } from '../../../../constants/global'
import StockDeductionCards from './StockDeductionCards'
import StockDeductionFailureTable from './StockDeductionFailureTable'
import StockDeductionProductTable from './StockDeductionProductTable'
import { useStockReportDetail } from './useStockReport'

const PLATFORM_LABEL: Record<string, string> = {
  tiktok: 'TikTok',
  shopee: 'Shopee',
}

// รายละเอียดการตัดสต๊อกของวันเดียว — เปิดจากตารางรายวันในแท็บรายงานตัดสต๊อก
// platform/shopId มาทาง query string เพราะหน้านี้เปิดเป็นแท็บใหม่ (ไม่มี state เดิม)
const StockDeductionDetailPage = () => {
  const { date } = useParams<{ date: string }>()
  const [searchParams] = useSearchParams()
  const platform = searchParams.get('platform') ?? 'tiktok'
  const shopId = searchParams.get('shopId') ?? undefined
  const platformLabel = PLATFORM_LABEL[platform] ?? platform

  const { data, error, isLoading } = useStockReportDetail({
    platform,
    startDate: date,
    endDate: date,
    shopId,
  })

  const dateLabel = date ? dayjs(date).format(ONLY_DATE_FORMAT) : ''

  return (
    <ContentWrapper>
      <Helmet title={`ตัดสต๊อก ${platformLabel} ${dateLabel}`} />
      <HeaderContent
        title={`รายละเอียดการตัดสต๊อก ${platformLabel}`}
        descriptions={`ภาพรวมและรายการสินค้าที่ถูกตัด/คืนสต๊อกในวันที่ ${dateLabel}`}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !isLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <StockDeductionCards summary={data?.summary} />

          <Typography variant="subtitle1" mb={1}>
            แยกตามสินค้า
          </Typography>
          <StockDeductionProductTable data={data?.products ?? []} />

          {!!data?.failures?.length && (
            <Box mt={4}>
              <Typography variant="subtitle1" mb={1}>
                รายการที่ตัดสต๊อกไม่ได้
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                SKU เหล่านี้ขายไปแล้วแต่ระบบหาสินค้าที่ผูกไว้ไม่เจอ
                จึงไม่ได้ตัดสต๊อก — ผูกสินค้าได้ที่หน้ารายการสินค้า (ปุ่ม 🔗)
              </Alert>
              <StockDeductionFailureTable data={data.failures} />
            </Box>
          )}
        </>
      )}
    </ContentWrapper>
  )
}

export default StockDeductionDetailPage
